/**
 * Lazy Loading with Retry and Auto-Refresh
 * 
 * This utility handles the common SPA deployment issue where:
 * 1. New deployment changes chunk file hashes
 * 2. Users with the app open still reference old chunk files
 * 3. Navigation fails with "Failed to fetch dynamically imported module"
 * 
 * Solution:
 * 1. Retry loading the chunk up to 3 times with exponential backoff
 * 2. If retries fail, auto-refresh the page to get new chunk references
 * 3. Track refresh attempts to avoid infinite refresh loops
 */

import React, { ComponentType, lazy, LazyExoticComponent } from 'react';

// Storage key to track refresh attempts (prevents infinite refresh loops)
const REFRESH_KEY = 'cwy_chunk_refresh_attempts';
const DEV_STALE_DEP_RELOAD_KEY = 'cwy_vite_stale_dep_reload';
const MAX_REFRESH_ATTEMPTS = 2;
const MAX_DEV_STALE_DEP_RELOADS = 1;
const REFRESH_RESET_TIME = 60000; // Reset counter after 1 minute

/** Vite HMR/transform failures look identical to stale production chunks. Never auto-reload in local dev. */
const isViteDev = (): boolean => Boolean(import.meta.env.DEV);

interface RefreshState {
  count: number;
  timestamp: number;
}

function getRefreshState(): RefreshState {
  try {
    const stored = sessionStorage.getItem(REFRESH_KEY);
    if (stored) {
      const state = JSON.parse(stored) as RefreshState;
      // Reset if more than 1 minute has passed
      if (Date.now() - state.timestamp > REFRESH_RESET_TIME) {
        return { count: 0, timestamp: Date.now() };
      }
      return state;
    }
  } catch {
    // Ignore storage errors
  }
  return { count: 0, timestamp: Date.now() };
}

function incrementRefreshCount(): number {
  const state = getRefreshState();
  const newState = { count: state.count + 1, timestamp: Date.now() };
  try {
    sessionStorage.setItem(REFRESH_KEY, JSON.stringify(newState));
  } catch {
    // Ignore storage errors
  }
  return newState.count;
}

function resetRefreshCount(): void {
  try {
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(DEV_STALE_DEP_RELOAD_KEY);
  } catch {
    // Ignore storage errors
  }
}

/** Vite dev: browserHash can stay the same after cache wipe, so immutable dep URLs stay cached with stale chunk refs. */
function attemptViteDevStaleDepReload(err: Error): boolean {
  try {
    const reloaded = Number(sessionStorage.getItem(DEV_STALE_DEP_RELOAD_KEY) || '0');
    if (reloaded >= MAX_DEV_STALE_DEP_RELOADS) {
      return false;
    }
    sessionStorage.setItem(DEV_STALE_DEP_RELOAD_KEY, String(reloaded + 1));
  } catch {
    return false;
  }

  console.warn(
    '[ChunkRecovery] Vite dev stale optimize cache — reloading once to fetch fresh dependency chunks.',
    err.message
  );
  window.location.reload();
  return true;
}

/**
 * Check if an error is a chunk load error
 */
export function isChunkLoadError(error: Error): boolean {
  const message = error.message?.toLowerCase() || '';
  const name = error.name?.toLowerCase() || '';
  
  return (
    name.includes('chunkloaderror') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('failed to load module script') ||
    // Safari / iOS WebKit (often overlaps with flaky mobile networks or stale chunks)
    message.includes('importing a module script failed') ||
    message.includes('not a valid javascript mime type') ||
    message.includes('text/html') ||
    message.includes('invalid mime type')
  );
}

function toError(reason: unknown): Error {
  if (reason instanceof Error) return reason;
  if (typeof reason === 'string') return new Error(reason);
  if (
    reason &&
    typeof reason === 'object' &&
    'message' in reason &&
    typeof (reason as { message: unknown }).message === 'string'
  ) {
    const msg = (reason as { message: string }).message;
    const err = new Error(msg);
    if (
      'name' in reason &&
      typeof (reason as { name: unknown }).name === 'string'
    ) {
      err.name = (reason as { name: string }).name;
    }
    return err;
  }
  return new Error(String(reason));
}

/**
 * When Vite/React dynamic imports receive HTML (404 → SPA index) or a stale chunk URL,
 * browsers reject with e.g. "text/html is not a valid JavaScript MIME type". That often
 * surfaces as unhandledrejection, not a classic script error event — use this from a
 * global handler to reload with the same limits as lazyWithRetry.
 *
 * @returns true if a reload was triggered (caller may preventDefault on the event)
 */
export function attemptStaleChunkReload(reason: unknown): boolean {
  const err = toError(reason);
  if (!isChunkLoadError(err)) return false;

  if (isViteDev()) {
    if (attemptViteDevStaleDepReload(err)) {
      return true;
    }
    console.warn(
      '[ChunkRecovery] Vite dev import still failing after reload — clear site data for localhost:5173 (cached immutable deps).',
      err.message
    );
    return false;
  }

  const refreshCount = getRefreshState().count;
  if (refreshCount >= MAX_REFRESH_ATTEMPTS) {
    console.error('[ChunkRecovery] Max refresh attempts reached:', err.message);
    return false;
  }

  incrementRefreshCount();
  console.log(
    `[ChunkRecovery] Auto-refreshing page (${refreshCount + 1}/${MAX_REFRESH_ATTEMPTS})`,
    err.message
  );
  window.location.reload();
  return true;
}

/**
 * Lazy load a component with retry logic and auto-refresh on failure
 * 
 * @param importFn - The dynamic import function, e.g., () => import('./MyComponent')
 * @param retries - Number of retry attempts before giving up (default: 3)
 * @returns LazyExoticComponent that handles chunk load failures gracefully
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3
): LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: Error | null = null;
    // Vite 504 "Outdated Optimize Dep" cannot be fixed by retrying the same
    // specifier (or by appending ?retry= — that stacks Vite's ?import and fails harder).
    const maxAttempts = isViteDev() ? 1 : retries;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 100));
        }

        const module = await importFn();
        resetRefreshCount();
        return module;
      } catch (error) {
        lastError = error as Error;
        console.warn(`[LazyWithRetry] Attempt ${attempt + 1}/${maxAttempts} failed:`, error);
      }
    }
    
    // All retries failed - check if it's a chunk load error
    if (lastError && isChunkLoadError(lastError)) {
      console.error('[LazyWithRetry] Chunk load failed after retries, attempting recovery');
      if (attemptStaleChunkReload(lastError)) {
        return new Promise(() => {});
      }
    }
    
    // Re-throw the error to be caught by ErrorBoundary
    throw lastError;
  });
}

/**
 * Pre-configured lazy loaders for common pages
 * Usage: const MyPage = lazyWithRetry(() => import('./pages/MyPage'));
 */
export default lazyWithRetry;

